/**
 * 기하 hit.
 *
 * `createGroup({ hitCanvas: false })`로 만든 그룹(브러시 획 묶음)은 hit 캔버스에 그리지 않는다. 대신 하위 Line을
 * 격자 인덱스(셀 256 스테이지 로컬 px) + 선분 거리로 판정하고, 스테이지 getIntersection에서 Konva hit와 합친다.
 * - 왜: 브러시 438개 층에서 제스처 끝·선택·드래그 끝마다 hit 캔버스를 다시 그리는 비용이 220ms였다.
 *   브러시 hit만 빼면 87~95ms(게이트 0).
 * - listening은 건드리지 않는다. Konva 포인터 처리(Stage._pointerdown 등)가 `!shape.isListening()`인 대상을 버리고,
 *   엔진 여러 곳이 `entity.node.listening()`을 "선택 가능" 의미로 쓰며 핀치 끝마다 true로 되돌리기 때문이다.
 *   그래서 hit 캔버스 그리기만 생략하고, 판정 결과로 Konva.Line 자체를 돌려준다(이벤트·버블링·드래그는 Konva 그대로).
 * - 판정 의미는 Konva hit와 같게 맞춘다: Line마다 hitStrokeWidth('auto'면 strokeWidth) 반폭 × 변환 배율,
 *   보이기·listening(레이어 포함)·hasHitStroke 검사, 레이어는 위에서부터, 같은 레이어 안에서는 그리기 순서(z)가 위인 쪽.
 *   곡선(tension) 획은 점을 직선으로 이어 근사한다(Konva hitFunc 'strokeScale'도 직선 경로다).
 * - 인덱스 갱신: 그룹·조상 루트의 변환, Line의 points·굵기 변경, 그룹 자식 추가 이벤트로 그룹을 dirty로 표시하고
 *   다음 판정 때 그 그룹만 다시 만든다. 이벤트 없이 destroy된 Line(지우개)은 판정 시 부모 불일치로 걸러 재구축한다.
 */
import Konva from 'konva'
import type { GroupConfig as KonvaGroupConfig } from 'konva/lib/Group'
import type { Vector2d } from '../types'

const CELL = 256
// 한 인덱스 항목에 담는 최대 점 수(긴 획을 쪼개 셀·AABB를 작게 유지)
const CHUNK_POINTS = 32
const NS = '.geomhit'
const TRANSFORM_EVENTS = [
  'x',
  'y',
  'rotation',
  'scaleX',
  'scaleY',
  'skewX',
  'skewY',
  'offsetX',
  'offsetY',
]
  .map(attr => `${attr}Change${NS}`)
  .join(' ')
// 브러시 Line은 자기 변환을 바꾸지 않는다(이동·변형은 그룹에 걸었다가 points로 굽는다). 굵기·점만 본다
const LINE_EVENTS = `pointsChange${NS} strokeWidthChange${NS} hitStrokeWidthChange${NS}`
// 유휴 사전 구축 1조각 예산(ms). 층 진입 직후 첫 탭이 인덱스 전체 구축(A21s 브러시 438개 층 약 220ms)을 떠안지 않게 한다
const PREBUILD_SLICE_MS = 8
/** 남은 dirty가 전부 스테이지 밖(제스처 홀더 분리 중)이거나 제스처 중일 때 사전 구축 재시도 간격 */
const PREBUILD_RETRY_MS = 300
/**
 * 유휴 콜백이 타임아웃으로만 불릴 때(유휴 구간이 없음) 다음 조각까지 간격. A21s WebView는 층 진입 뒤 rIC가
 * 전부 didTimeout(초당 2회)이라 8ms 조각만으로는 브러시 438개 층 구축이 약 24초 걸렸다 — 그동안 첫 탭이 남은 구축을 떠안는다
 */
const PREBUILD_BUSY_GAP_MS = 40
// 마지막 그룹 등록 뒤 이만큼 조용해야 사전 구축을 시작(층 진입 조각 사이 간격 ≈10ms보다 넉넉히)
const PREBUILD_REGISTER_QUIET_MS = 150

interface Item {
  group: KonvaGeomHitGroup
  line: Konva.Line
  /** 스테이지 로컬 좌표 [x0,y0,x1,y1,...] (청크 경계 점은 양쪽 청크에 중복) */
  pts: number[]
  half: number
  minX: number
  minY: number
  maxX: number
  maxY: number
  keys: string[]
}

/** hit 캔버스에 그리지 않는 그룹. 판정은 KonvaGeomHit가 대신한다 */
export class KonvaGeomHitGroup extends Konva.Group {
  constructor(config?: KonvaGroupConfig) {
    super(config)
  }

  drawHit(): this {
    return this
  }
}

function segDist2(px: number, py: number, ax: number, ay: number, bx: number, by: number): number {
  const dx = bx - ax
  const dy = by - ay
  const len2 = dx * dx + dy * dy
  let t = len2 ? ((px - ax) * dx + (py - ay) * dy) / len2 : 0
  t = t < 0 ? 0 : t > 1 ? 1 : t
  const qx = ax + t * dx - px
  const qy = ay + t * dy - py
  return qx * qx + qy * qy
}

/** 레이어 바로 아래 자식부터 node까지의 인덱스 경로 */
function zPath(node: Konva.Node): number[] {
  const path: number[] = []
  let cur: Konva.Node | null = node
  while (cur && cur.nodeType !== 'Layer' && cur.nodeType !== 'Stage') {
    path.push(cur.index)
    cur = cur.getParent()
  }
  return path.reverse()
}

/** 같은 레이어에서 a가 b보다 위에 그려지는지 */
function isAbove(a: Konva.Node, b: Konva.Node): boolean {
  const pa = zPath(a)
  const pb = zPath(b)
  const n = Math.min(pa.length, pb.length)
  for (let i = 0; i < n; i++) {
    if (pa[i] !== pb[i]) return pa[i] > pb[i]
  }
  return pa.length > pb.length
}

export class KonvaGeomHit {
  private readonly groups = new Set<KonvaGeomHitGroup>()
  private readonly dirty = new Set<KonvaGeomHitGroup>()
  private readonly itemsByGroup = new Map<KonvaGeomHitGroup, Item[]>()
  private readonly grid = new Map<string, Item[]>()
  /** 그룹별로 변환 이벤트를 걸어 둔 조상(브러시 = 엔티티 루트) */
  private readonly watchedParent = new Map<KonvaGeomHitGroup, Konva.Container>()
  private registerCount = 0
  private lastRegisterAt = 0
  /** 다음 사전 구축 조각에서 prune할지(register가 256번마다 세움) */
  private pruneDue = false
  private readonly watchedGroups = new WeakSet<KonvaGeomHitGroup>()
  private readonly watchedLines = new WeakSet<Konva.Line>()
  private prebuildScheduled = false

  constructor(private readonly stage: Konva.Stage) {
    const nativeGetIntersection = stage.getIntersection.bind(stage)
    stage.getIntersection = (pos: Vector2d | null): Konva.Shape | null => {
      if (!pos) return null
      if (this.groups.size === 0) return nativeGetIntersection(pos)
      return this.intersect(pos)
    }
  }

  register(group: KonvaGeomHitGroup): void {
    // 층 진입은 브러시 수백 개를 한 동기 구간에 만든다 — 파괴된 그룹 정리(prune)는 사전 구축 조각으로, 그룹 변경
    // 감시는 첫 구축(rebuild) 때로 미룬다. 그 전까지 그룹은 dirty라 감시가 없어도 판정 전에 다시 구축된다
    if (++this.registerCount % 256 === 0) this.pruneDue = true
    this.lastRegisterAt = performance.now()
    this.groups.add(group)
    this.dirty.add(group)
    this.schedulePrebuild()
  }

  /** 그룹 자신의 자식 추가·변환 변경 → dirty(멱등) */
  private watchGroup(group: KonvaGeomHitGroup): void {
    if (this.watchedGroups.has(group)) return
    this.watchedGroups.add(group)
    group.on(`add${NS} ${TRANSFORM_EVENTS}`, () => {
      this.dirty.add(group)
    })
  }

  /** dirty 그룹을 유휴 시간에 조각으로 미리 구축한다(판정 시 flush가 남은 것을 마저 처리하므로 정합성과 무관) */
  private schedulePrebuild(): void {
    if (this.prebuildScheduled) return
    this.prebuildScheduled = true
    const slice = (deadline?: IdleDeadline): void => {
      if (!this.stage.container()) {
        this.prebuildScheduled = false
        return
      }
      if (!this.isInteractive()) {
        // 제스처 중엔 프레임과 다투지 않도록 쉰다(끝나면 이어서)
        setTimeout(schedule, PREBUILD_RETRY_MS)
        return
      }
      // 그룹이 막 등록되는 중(층 진입 도형 생성 조각)이면 쉰다 — rIC가 timeout으로 조각 바로 뒤에 붙어 그 프레임을 늘렸다
      if (performance.now() - this.lastRegisterAt < PREBUILD_REGISTER_QUIET_MS) {
        setTimeout(slice, PREBUILD_REGISTER_QUIET_MS)
        return
      }
      if (this.pruneDue) {
        this.pruneDue = false
        this.prune()
      }
      const end = performance.now() + PREBUILD_SLICE_MS
      let built = 0
      for (const group of [...this.dirty]) {
        if (performance.now() > end) break
        if (this.rebuild(group)) {
          this.dirty.delete(group)
          built++
        }
      }
      if (this.dirty.size === 0) this.prebuildScheduled = false
      // 남은 것이 전부 스테이지 밖(제스처 홀더 분리 중 등)이면 잠시 뒤 다시 본다
      else if (built === 0) setTimeout(schedule, PREBUILD_RETRY_MS)
      else if (!deadline || deadline.didTimeout) setTimeout(slice, PREBUILD_BUSY_GAP_MS)
      else schedule()
    }
    const schedule = (): void => {
      if (typeof requestIdleCallback === 'function') requestIdleCallback(slice, { timeout: 500 })
      else setTimeout(slice, 16)
    }
    schedule()
  }

  /** 판정이 의미 있는 상태인지(보이고 listening인 레이어가 하나라도 있음). 제스처 중엔 전 레이어가 listening(false) */
  private isInteractive(): boolean {
    return this.stage.getLayers().some(layer => layer.isListening() && layer.isVisible())
  }

  /** 스테이지 좌표(컨테이너 CSS px) 판정. 포인터 이벤트와 같은 결과(Konva hit + 기하 hit 병합) */
  intersect(pos: Vector2d): Konva.Shape | null {
    // 제스처 중(전 레이어 listening(false) — hitOnDragEnabled라 팬 pointermove마다 불린다)엔 Konva도 null.
    // flush·질의를 건너뛴다(분리 홀더 그룹이 dirty로 남아 있으면 매 이동마다 순회하게 됨)
    if (!this.isInteractive()) return null
    const layers = this.stage.getLayers()
    const geo = this.query(pos)
    for (let n = layers.length - 1; n >= 0; n--) {
      const layer = layers[n]
      const native = layer.getIntersection(pos)
      let best: Konva.Line | null = null
      for (const line of geo) {
        if (line.getLayer() !== layer) continue
        if (!best || isAbove(line, best)) best = line
      }
      if (native && best) return isAbove(best, native) ? best : native
      if (native) return native
      if (best) return best
    }
    return null
  }

  /** pos(스테이지 좌표)에 걸리는 기하 hit 대상 Line들(보이기·listening·hasHitStroke 통과분) */
  private query(pos: Vector2d): Konva.Line[] {
    this.flush()
    const local = this.stage.getAbsoluteTransform().copy().invert().point(pos)
    const cell = this.grid.get(`${Math.floor(local.x / CELL)},${Math.floor(local.y / CELL)}`)
    if (!cell) return []
    const hits: Konva.Line[] = []
    let stale = false
    for (const item of cell) {
      if (local.x < item.minX || local.x > item.maxX || local.y < item.minY || local.y > item.maxY)
        continue
      if (hits.includes(item.line)) continue
      // 루트째 레이어에서 떨어진 엔티티(제스처 홀더 분리 중 등)는 hit 캔버스에도 없다. stale 검사보다 먼저 —
      // 스테이지 밖 그룹은 rebuild가 보류돼 다시 판정해도 같은 항목이 남는다(무한 재귀)
      if (item.group.getStage() !== this.stage) continue
      if (item.line.getParent() !== item.group) {
        // 이벤트 없이 destroy/이동된 Line(지우개) — 그룹을 다시 만들고 다시 판정
        this.dirty.add(item.group)
        stale = true
        continue
      }
      const h2 = item.half * item.half
      const p = item.pts
      let hit = false
      for (let i = 0; i + 3 < p.length; i += 2) {
        if (segDist2(local.x, local.y, p[i], p[i + 1], p[i + 2], p[i + 3]) <= h2) {
          hit = true
          break
        }
      }
      if (!hit) continue
      // Konva hit 캔버스에 그려질 조건과 같게(보이기·listening은 레이어·조상 포함)
      if (!item.line.shouldDrawHit() || !item.line.hasHitStroke()) continue
      hits.push(item.line)
    }
    if (stale) return this.query(pos)
    return hits
  }

  private flush(): void {
    if (this.dirty.size === 0) return
    for (const group of [...this.dirty]) {
      if (this.rebuild(group)) this.dirty.delete(group)
    }
  }

  private removeItems(group: KonvaGeomHitGroup): void {
    const items = this.itemsByGroup.get(group)
    if (!items) return
    for (const item of items) {
      for (const key of item.keys) {
        const cell = this.grid.get(key)
        if (!cell) continue
        const i = cell.indexOf(item)
        if (i >= 0) cell.splice(i, 1)
        if (cell.length === 0) this.grid.delete(key)
      }
    }
    this.itemsByGroup.delete(group)
  }

  private unregister(group: KonvaGeomHitGroup): void {
    this.removeItems(group)
    this.groups.delete(group)
    this.dirty.delete(group)
    group.off(NS)
    this.watchedGroups.delete(group)
    this.watchedParent.get(group)?.off(NS)
    this.watchedParent.delete(group)
  }

  private prune(): void {
    for (const group of [...this.groups]) {
      if (!group.getParent()) this.unregister(group)
    }
  }

  /** 그룹 항목을 다시 만든다. false = 스테이지 밖이라 보류(dirty로 남겨 다음 flush·사전 구축이 다시 시도) */
  private rebuild(group: KonvaGeomHitGroup): boolean {
    const parent = group.getParent()
    if (!parent) {
      // destroy된 것(브러시 그룹은 루트에서 떼지 않는다). 생성 직후 루트에 붙는 것은 같은 동기 구간이라
      // 판정(포인터 이벤트)이 그 사이에 끼어들 수 없다
      this.unregister(group)
      return true
    }
    this.watchGroup(group)
    // 엔티티 루트(그룹의 부모) 변환도 감시. 제스처 홀더 이동처럼 부모가 바뀌면 다시 건다
    const watched = this.watchedParent.get(group)
    if (watched !== parent) {
      watched?.off(NS)
      const markGroup = (): void => {
        this.dirty.add(group)
      }
      parent.on(TRANSFORM_EVENTS, markGroup)
      this.watchedParent.set(group, parent)
    }
    // 루트째 스테이지 밖(제스처 홀더 분리 중 등) — 기존 항목은 남겨 둔다(query의 getStage 필터가 거르고,
    // 다시 붙으면 변환이 그대로라 유효하다). 비우고 확정하면 다시 붙어도 이벤트가 없어 영영 탭이 안 된다
    if (!group.getStage()) return false
    this.removeItems(group)
    const toLocal = this.stage.getAbsoluteTransform().copy().invert()
    const items: Item[] = []
    for (const child of group.getChildren()) {
      if (!(child instanceof Konva.Line)) continue
      const line = child
      if (!this.watchedLines.has(line)) {
        // Line은 그룹 사이를 옮기지 않으므로(지우개도 같은 그룹에 새 Line을 만든다) 처음 본 그룹에 한 번만 건다
        this.watchedLines.add(line)
        line.on(LINE_EVENTS, () => {
          this.dirty.add(group)
        })
      }
      const raw = line.points()
      if (raw.length < 2) continue
      const m = toLocal.copy().multiply(line.getAbsoluteTransform()).getMatrix()
      const hsw = line.hitStrokeWidth()
      const width = hsw === 'auto' ? line.strokeWidth() : hsw
      const scale = Math.sqrt(Math.abs(m[0] * m[3] - m[1] * m[2]))
      const half = ((width || 0) * scale) / 2
      const pts: number[] = []
      for (let i = 0; i + 1 < raw.length; i += 2) {
        const x = raw[i]
        const y = raw[i + 1]
        pts.push(m[0] * x + m[2] * y + m[4], m[1] * x + m[3] * y + m[5])
      }
      if (line.closed() && pts.length >= 4) pts.push(pts[0], pts[1])
      if (pts.length === 2) pts.push(pts[0], pts[1])
      // 청크로 쪼갬(경계 점 공유)
      const step = CHUNK_POINTS * 2
      for (let s = 0; s + 2 < pts.length; s += step - 2) {
        const chunk = pts.slice(s, Math.min(pts.length, s + step))
        let minX = Infinity
        let minY = Infinity
        let maxX = -Infinity
        let maxY = -Infinity
        for (let i = 0; i < chunk.length; i += 2) {
          if (chunk[i] < minX) minX = chunk[i]
          if (chunk[i] > maxX) maxX = chunk[i]
          if (chunk[i + 1] < minY) minY = chunk[i + 1]
          if (chunk[i + 1] > maxY) maxY = chunk[i + 1]
        }
        const item: Item = {
          group,
          line,
          pts: chunk,
          half,
          minX: minX - half,
          minY: minY - half,
          maxX: maxX + half,
          maxY: maxY + half,
          keys: [],
        }
        for (let gx = Math.floor(item.minX / CELL); gx <= Math.floor(item.maxX / CELL); gx++) {
          for (let gy = Math.floor(item.minY / CELL); gy <= Math.floor(item.maxY / CELL); gy++) {
            const key = `${gx},${gy}`
            let cell = this.grid.get(key)
            if (!cell) this.grid.set(key, (cell = []))
            cell.push(item)
            item.keys.push(key)
          }
        }
        items.push(item)
        if (s + step >= pts.length) break
      }
    }
    this.itemsByGroup.set(group, items)
    return true
  }
}
